'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { t, countryNameIn } from '@/data/translations';

interface CountryBadgeProps {
  countryCode: string;
  countryName: string;
  flag: string;
  onChangeCountry: (code: string) => void;
  countries: { code: string; name: string; flag: string }[];
  langCode: string;
}

export default function CountryBadge({
  countryCode,
  countryName,
  flag,
  onChangeCountry,
  countries,
  langCode,
}: CountryBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Localize each country's name in the user's UI language for display + search.
  const localized = useMemo(
    () => countries.map(c => ({ ...c, displayName: countryNameIn(c.code, langCode) })),
    [countries, langCode]
  );

  const filtered = localized.filter(c => {
    const q = search.toLowerCase();
    return c.displayName.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
  });

  return (
    <>
      {/* Badge button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/50 backdrop-blur-sm hover:border-slate-600 transition-colors"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-sm">{flag}</span>
        <span className="text-sm text-slate-300 font-medium">{countryName}</span>
        <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Country selector overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="bg-slate-900 rounded-3xl border border-slate-700/50 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-800">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-white font-bold text-lg" lang={langCode}>{t('yourCountry', langCode)}</h2>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-slate-500 hover:text-white p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('search', langCode)}
                    className="w-full bg-slate-800 text-white rounded-xl px-4 py-2.5 border border-slate-700 focus:border-sky-500 outline-none text-sm"
                    autoFocus
                    lang={langCode}
                  />
                </div>

                {/* Country grid */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="flex flex-wrap gap-2">
                    {filtered.map(c => (
                      <button
                        key={c.code}
                        onClick={() => {
                          onChangeCountry(c.code);
                          setIsOpen(false);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                          c.code === countryCode
                            ? 'bg-sky-500/20 border border-sky-500/50 text-sky-300'
                            : 'bg-slate-800 border border-slate-700/50 text-slate-300 hover:bg-slate-750 hover:text-white'
                        }`}
                      >
                        <span>{c.flag}</span>
                        <span lang={langCode}>{c.displayName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
